/* eslint-disable react/prefer-stateless-function */
import React from 'react';
import {Map, List} from 'immutable';
import {flow} from 'lodash';

import appStore from 'client/AppStore';
import {callAction, getStoreByName} from 'client/framework';
import SettingsTable from 'sg/Tables/SettingsTable/SettingsTable.react';
import DownloadCSV from 'sg/Tables/SettingsTable/DownloadCSV.react';
import {Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import {sortDirections} from 'sg/Tables/tableConstants';
import Button from 'sg/Button/Button.react';
import masqueradeActions from 'generic/Masquerade/Masquerade.actions';
import {WithTooltip, Text} from '@albert-io/atomic';

import ExpandSubjectsFilter from '../Filters/ExpandSubjectsFilter.react';
import ExpandTeachersFilter from '../Filters/ExpandTeachersFilter.react';
import usageReportsStore from '../UsageReports.store';
import ClassroomStatusFilters from '../Filters/ClassroomStatusFilters.react';

import EmptyUsageReport from './EmptyUsageReport.react';
import {
  getCountOfSchoolStudentsContent,
  getCountOfSchoolAssignmentsContent,
  getSchoolSubjectsContent,
  getStudentAttemptsContent,
  getPerformanceBarContent,
  getCSVHeaders
} from './utils';

const usageReportStoreName = 'ClassroomUsageReportTableStore';

function expandTeachersMutator(data) {
  if (!usageReportsStore.areTeachersExpanded()) {
    return data;
  }
  return data.reduce((acc, classroom) => {
    const teachers = classroom.getTeachers();
    return teachers.size > 1
      ? acc.concat(
          teachers.map((teacher) => {
            return classroom.setField('teachers', List.of(teacher));
          })
        )
      : acc.push(classroom);
  }, List());
}

function expandSubjectsMutator(data) {
  if (!usageReportsStore.areSubjectsExpanded()) {
    return data;
  }
  const searchTerm = getStoreByName(usageReportStoreName).getSearchTerm();
  return usageReportsStore.getClassesExpandSubjectsMutator(searchTerm)(data);
}

const dataMutator = flow(expandTeachersMutator, expandSubjectsMutator);

export default class ClassesReport extends React.Component {
  render() {
    const areSubjectsExpanded = usageReportsStore.areSubjectsExpanded();
    return (
      <SettingsTable
        title='Usage'
        topFilters={<ClassroomStatusFilters />}
        bottomFilters={
          <>
            <ExpandSubjectsFilter />
            <ExpandTeachersFilter />
          </>
        }
        defaultSortDirection={sortDirections.DESCENDING}
        query={usageReportsStore.getClassroomsQuery()}
        dataMutator={dataMutator}
        searchFilters={usageReportsStore.getClassroomSearchFilters}
        storeName={usageReportStoreName}
        optionsMenu={
          <DownloadCSV
            csvQuery={usageReportsStore.getClassroomsCSVQuery}
            tableStoreName={usageReportStoreName}
            fileName='classes-usage-report'
            getCSVHeaders={getCSVHeaders('classes')}
          />
        }
        className='usage-report__table'
        emptyTableElement={
          <EmptyUsageReport
            headerText={`You don't have any classes!`}
            bodyText={`Classes taught by teachers you've invited or approved will appear here.`}
          />
        }
      >
        <Column
          heading='Class'
          sortBy='name'
          alwaysVisible
          content={(classroom) => classroom.getName()}
        />
        <Column
          heading='Teachers'
          defaultVisible
          content={(classroom) => {
            const teachers = classroom
              .getTeachers()
              .sortBy((teacher) => teacher.getFullName({formal: true}));
            return (
              <div className='usage-report__user-cell'>
                <span className='usage-report__user-name'>
                  {teachers.first().getFullName({formal: true})}
                </span>
                {teachers.size === 1 ? (
                  <span className='usage-report__user-email'>{teachers.first().getEmail()}</span>
                ) : (
                  <span>
                    <WithTooltip
                      content={
                        <>
                          {teachers.map((teacher, i, arr) => (
                            <span>
                              <Text color='white' size='s' bold>
                                {teacher.getFullName({formal: true})}
                              </Text>{' '}
                              (
                              <Text color='white' size='s'>
                                {teacher.getEmail()}
                              </Text>
                              ){arr.size !== i + 1 && ','}&nbsp;
                            </span>
                          ))}
                        </>
                      }
                      placement='right'
                    >
                      <span className='usage-report__user-email'>+{teachers.size - 1} more</span>
                    </WithTooltip>
                  </span>
                )}
              </div>
            );
          }}
        />
        <Column
          heading='Subjects'
          sortBy={!areSubjectsExpanded ? 'meta.school_subject_names' : null}
          defaultVisible
          content={(classroom) => getSchoolSubjectsContent(classroom, areSubjectsExpanded)}
        />
        <Column
          heading='Students'
          sortBy={!areSubjectsExpanded ? 'meta.count_of_school_students' : null}
          defaultVisible
          className='sg-table__td--align-right'
          content={(classroom) => getCountOfSchoolStudentsContent(classroom, areSubjectsExpanded)}
        />
        <Column
          defaultSortParam
          heading='Student Attempts'
          sortBy={!areSubjectsExpanded ? 'meta.count_of_school_guesses' : null}
          defaultVisible
          className='sg-table__td--align-right'
          content={(classroom) => getStudentAttemptsContent(classroom, areSubjectsExpanded)}
        />
        <Column
          heading='Assignments'
          sortBy={!areSubjectsExpanded ? 'meta.count_of_school_assignments' : null}
          className='sg-table__td--align-right'
          content={(classroom) =>
            getCountOfSchoolAssignmentsContent(classroom, areSubjectsExpanded)
          }
        />
        <Column
          heading='Performance'
          content={(classroom) => getPerformanceBarContent(classroom, areSubjectsExpanded)}
        />
        <Column
          alwaysVisible
          content={(classroom) => {
            const onViewClassClick = () => {
              const redirect = appStore.routerProps.location.pathname;
              callAction(masqueradeActions.ENABLE_MASQUERADING, {
                user: Map({
                  id: classroom.getTeachers().first().get('id')
                }),
                school: Map({
                  id: appStore.routerProps.params.schoolId
                }),
                origin: redirect
              });
            };

            return (
              <>
                <Button
                  to={`/reports/classrooms/${classroom.getId()}/students`}
                  className='usage-report__table-button'
                  text='View Report'
                  outline
                />
                <Button
                  to={`/classes/${classroom.getId()}/assignments`}
                  className='usage-report__table-button'
                  text='Go To Class'
                  outline
                  onClick={onViewClassClick}
                />
              </>
            );
          }}
        />
      </SettingsTable>
    );
  }
}
